/**
 * Typed API client with JWT authentication and automatic token refresh.
 * All requests go through Vite proxy (/api → localhost:3000).
 */

const TOKEN_KEY = 'witehik_token';
const REFRESH_TOKEN_KEY = 'witehik_refresh_token';
const ADMIN_TOKEN_KEY = 'witehik_admin_token';

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  clearAdminToken();
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string): void {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
}

export function getAdminToken(): string | null {
  return sessionStorage.getItem(ADMIN_TOKEN_KEY);
}

export function setAdminToken(token: string): void {
  sessionStorage.setItem(ADMIN_TOKEN_KEY, token);
}

export function clearAdminToken(): void {
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

function authHeaders(): Record<string, string> {
  const token = getToken() || getAdminToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// Dedup simultaneous refresh calls
let refreshPromise: Promise<boolean> | null = null;

async function doRefresh(): Promise<boolean> {
  const rt = getRefreshToken();
  if (!rt) return false;

  try {
    const res = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: rt }),
    });

    if (!res.ok) return false;

    const data = await res.json();
    if (data.success && data.data?.accessToken) {
      setToken(data.data.accessToken);
      if (getAdminToken()) {
        setAdminToken(data.data.accessToken);
      }
      if (data.data.refreshToken) {
        setRefreshToken(data.data.refreshToken);
      }
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

async function refreshTokens(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = doRefresh();
  const result = await refreshPromise;
  refreshPromise = null;
  return result;
}

/** JSON request with auth + automatic 401 refresh */
export async function apiFetch<T = unknown>(url: string, options: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    ...authHeaders(),
    ...((options.headers as Record<string, string>) || {}),
  };

  // Don't set Content-Type for FormData (browser sets boundary automatically)
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }

  let res = await fetch(url, { ...options, headers });

  // On 401, try transparent token refresh
  if (res.status === 401 && !url.includes('/api/auth/login') && !url.includes('/api/auth/refresh')) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      headers.Authorization = `Bearer ${getToken()}`;
      res = await fetch(url, { ...options, headers });
    }
  }

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`API ${res.status}: ${text || res.statusText}`);
  }

  return res.json() as Promise<T>;
}

/** GET shorthand */
export function apiGet<T = unknown>(url: string): Promise<T> {
  return apiFetch<T>(url);
}

/** POST JSON shorthand */
export function apiPost<T = unknown>(url: string, body?: unknown): Promise<T> {
  return apiFetch<T>(url, {
    method: 'POST',
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

/** PUT JSON shorthand */
export function apiPut<T = unknown>(url: string, body?: unknown): Promise<T> {
  return apiFetch<T>(url, {
    method: 'PUT',
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

/** DELETE JSON shorthand */
export function apiDelete<T = unknown>(url: string, body?: unknown): Promise<T> {
  return apiFetch<T>(url, {
    method: 'DELETE',
    body: body != null ? JSON.stringify(body) : undefined,
  });
}

/** POST FormData with auth */
export function apiPostForm<T = unknown>(url: string, formData: FormData): Promise<T> {
  return apiFetch<T>(url, { method: 'POST', body: formData });
}
